import {
  createTheme,
  StyledEngineProvider,
  ThemeProvider,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
// import { useDispatch, useSelector } from 'src/store';
import { GoogleOAuthProvider } from "@react-oauth/google";
import { SnackbarProvider } from "notistack";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { BrowserRouter } from "react-router-dom";
import AppErrorBoundary from "src/components/AppErrorBoundary";
import CookiesNotification from "src/components/CookiesNotification";
import GlobalStyles from "src/components/GlobalStyles";
import ScrollReset from "src/components/ScrollReset";
import { InvoiceGenerationProvider } from "src/contexts/InvoiceGenerationContext";
import routes, { renderRoutes } from "src/routes";
import { buildTheme } from "src/theme";
import useSWR from "swr";
import LoadingScreen from "./components/LoadingScreen";
import coreConfig from "./coreConfig";
import {
  getCustomers,
  loadCustomers,
  setCustomerById,
} from "./slices/customers";
import { loadSettings, loadSiteConfig, saveSettings } from "./slices/settings";
import { useDispatch, useSelector } from "./store";
import { apiGet } from "./utils/axios";

const GLOBAL_IMAGE_FALLBACK_SRC = `data:image/svg+xml;utf8,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="640" height="360" viewBox="0 0 640 360"><rect width="640" height="360" fill="#f1f5f9"/><g fill="none" stroke="#94a3b8" stroke-width="14" stroke-linecap="round" stroke-linejoin="round"><rect x="145" y="92" width="350" height="176" rx="18"/><circle cx="250" cy="162" r="30"/><path d="M178 236l74-54 52 38 46-34 85 50"/></g><text x="320" y="308" text-anchor="middle" fill="#64748b" font-size="26" font-family="Arial, sans-serif">Image unavailable</text></svg>',
)}`;

const getPersistedThemeBootstrap = () => {
  if (typeof window === "undefined") {
    return {
      direction: "ltr",
      responsiveFontSizes: true,
      theme: "LIGHT",
      textScale: 1,
    };
  }

  try {
    const rawSettings = localStorage.getItem("settings");
    if (!rawSettings) {
      return {
        direction: "ltr",
        responsiveFontSizes: true,
        theme: "LIGHT",
        textScale: 1,
      };
    }

    const parsedSettings = JSON.parse(rawSettings);
    return {
      direction: parsedSettings.direction || "ltr",
      responsiveFontSizes: parsedSettings.responsiveFontSizes ?? true,
      theme: parsedSettings.theme || "LIGHT",
      textScale: parsedSettings.textScale || 1,
    };
  } catch (error) {
    return {
      direction: "ltr",
      responsiveFontSizes: true,
      theme: "LIGHT",
      textScale: 1,
    };
  }
};

const App = () => {
  const dispatch = useDispatch();
  const ws = useRef(null);
  const [tries, setTries] = useState(1);
  const retrySocket = useRef({});
  const {
    adminMode,
    siteConfig,
    siteConfigLoaded,
    direction,
    responsiveFontSizes,
    theme,
    textScale,
    settingsLoaded,
    csrf,
  } = useSelector((state) => state.settings);
  const [currentTheme, setCurrentTheme] = useState(() => createTheme());
  const { currentCustomer, customers, customersLoaded } = useSelector(
    (state) => state.customers,
  );
  const taxes = useSelector((state) => state.taxes);
  const [connected, setConnected] = useState(false);

  const { user, notifyMessage, loaded } = useSelector((state) => state.users);

  useEffect(() => {
    if (!settingsLoaded) {
      dispatch(loadSettings());
    }
  }, [settingsLoaded]);

  useEffect(() => {
    if (!siteConfigLoaded) {
      dispatch(loadSiteConfig());
      dispatch(loadCustomers());
    }
  }, [siteConfig, siteConfigLoaded]);

  useEffect(() => {
    const handleResourceError = (event) => {
      const target = event?.target;
      if (!(target instanceof HTMLImageElement)) {
        return;
      }

      if (target.dataset.fallbackApplied === "1") {
        return;
      }

      target.dataset.fallbackApplied = "1";
      target.removeAttribute("srcset");
      target.src = GLOBAL_IMAGE_FALLBACK_SRC;
    };

    window.addEventListener("error", handleResourceError, true);
    return () => {
      window.removeEventListener("error", handleResourceError, true);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    if (!window.__arkaduRuntimePerf) {
      window.__arkaduRuntimePerf = {
        lcpEntries: [],
        longTasks: [],
        cls: 0,
      };
    }

    const store = window.__arkaduRuntimePerf;
    const observers = [];

    if (typeof PerformanceObserver === "function") {
      try {
        const lcpObserver = new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries();
          entries.forEach((entry) => {
            store.lcpEntries.push({
              startTime: entry.startTime,
              element: entry.element?.tagName || null,
              size: entry.size || 0,
            });
          });
          if (store.lcpEntries.length > 50) {
            store.lcpEntries = store.lcpEntries.slice(-50);
          }
        });
        lcpObserver.observe({
          type: "largest-contentful-paint",
          buffered: true,
        });
        observers.push(lcpObserver);
      } catch (_error) {
        // Not supported in this browser/session.
      }

      try {
        const longTaskObserver = new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries();
          entries.forEach((entry) => {
            store.longTasks.push({
              startTime: entry.startTime,
              duration: entry.duration,
            });
          });
          if (store.longTasks.length > 300) {
            store.longTasks = store.longTasks.slice(-300);
          }
        });
        longTaskObserver.observe({ type: "longtask", buffered: true });
        observers.push(longTaskObserver);
      } catch (_error) {
        // Not supported in this browser/session.
      }

      try {
        const clsObserver = new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries();
          entries.forEach((entry) => {
            if (!entry.hadRecentInput) {
              store.cls += entry.value;
            }
          });
        });
        clsObserver.observe({ type: "layout-shift", buffered: true });
        observers.push(clsObserver);
      } catch (_error) {
        // Not supported in this browser/session.
      }
    }

    return () => {
      observers.forEach((observer) => {
        try {
          observer.disconnect();
        } catch (_error) {
          // no-op
        }
      });
    };
  }, []);

  useEffect(() => {
    const bootstrapConfig = getPersistedThemeBootstrap();
    const resolvedConfig = settingsLoaded
      ? {
          direction,
          responsiveFontSizes,
          theme,
          textScale,
        }
      : bootstrapConfig;

    const timerId = window.setTimeout(() => {
      setCurrentTheme(buildTheme(resolvedConfig, {}));
    }, 0);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [settingsLoaded, direction, responsiveFontSizes, theme, textScale]);

  if (!siteConfigLoaded) {
    return (
      <ThemeProvider theme={currentTheme}>
        <LoadingScreen
          message={"Cargando configuración"}
          withBackdrop
          suppressWhenGlobalLoading={false}
        />
      </ThemeProvider>
    );
  }

  return (
    <>
      <Helmet>
        <title>{coreConfig.siteName || siteConfig.name}</title>
        <meta
          name="description"
          content={coreConfig.siteDescription || siteConfig.slogan}
        />
        <meta
          name="keywords"
          content={
            coreConfig.siteKeywords ||
            siteConfig.keywords ||
            `Gestión, Recaudación, Impuestos, Servicios, Facturación, 
            Automatización, Control y seguimiento, Citas y control de turnos, 
            IoT, Alcaldías, Municipios, Catastro, Ambiente, Control urbano, 
            Control de obras, Actividades Económicas, Economía popular, 
            Vehículos, Publicidad, Licencias, Impuestos, 
            Recaudación, Control de obras, Control urbano, Catastro, 
            Ambiente, Economía popular, Vehículos, Publicidad, Licencias, 
            Impuestos, Recaudación, Control de obras, Control urbano, Catastro, 
            Terminales, Aseo urbano, Policía municipal, Control de actividades,
            Fiscalización.
            `
          }
        />

        <meta
          property="og:title"
          content={coreConfig.siteName || siteConfig.name}
        />
        <meta
          property="og:description"
          content={coreConfig.siteDescription || siteConfig.slogan}
        />
        <meta
          property="og:image"
          content={coreConfig.siteLogo || siteConfig.logo}
        />
        <meta property="og:url" content={window.location.href} />
        <meta
          name="twitter:title"
          content={coreConfig.siteName || siteConfig.name}
        />
        <meta
          name="twitter:description"
          content={coreConfig.siteDescription || siteConfig.slogan}
        />
        <meta
          name="twitter:image"
          content={coreConfig.siteLogo || siteConfig.logo}
        />
        <meta name="twitter:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={currentTheme}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <GoogleOAuthProvider
              clientId={
                "781913491866-42svrqbnpim0uak8tu9cejqjb5u783df.apps.googleusercontent.com"
              }
            >
              <SiteConfigRequired>
                <SnackbarProvider dense maxSnack={3}>
                  <BrowserRouter
                    future={{
                      v7_startTransition: true,
                      v7_relativeSplatPath: true,
                    }}
                  >
                    <InvoiceGenerationProvider>
                      <GlobalStyles />
                      <ScrollReset />
                      {/* <GoogleAnalytics /> */}
                      <CookiesNotification />
                      {/* <SettingsNotification /> */}
                      <AppErrorBoundary>
                        {renderRoutes(routes)}
                      </AppErrorBoundary>
                    </InvoiceGenerationProvider>
                  </BrowserRouter>
                </SnackbarProvider>
              </SiteConfigRequired>
            </GoogleOAuthProvider>
          </LocalizationProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </>
  );
};

export default App;

const SiteConfigRequired = ({ children }) => {
  //@ts-ignore
  const { siteConfig, adminMode } = useSelector((state) => state.settings);
  var customerId = window["customer_id"];
  const { currentCustomer, customers, customersLoaded } = useSelector(
    (state) => state.customers,
  );
  const dispatch = useDispatch();
  const emptyCustomersRefetched = React.useRef(false);
  React.useEffect(() => {
    if (!customersLoaded || !customers) {
      dispatch(getCustomers());
    } else if (customers.length === 0 && !emptyCustomersRefetched.current) {
      // Refetch an empty catalog only once; retries after errors are handled with backoff in getCustomers.
      emptyCustomersRefetched.current = true;
      dispatch(getCustomers());
    }
  }, [customersLoaded, customers, dispatch]);

  React.useEffect(() => {
    if (customers && customers.length > 0 && customerId && !currentCustomer) {
      dispatch(setCustomerById(customerId));
    }
  }, [customers, customerId, currentCustomer]);

  useEffect(() => {
    if (adminMode) {
      // reset config version to 0
      if (siteConfig.version !== 0) {
        dispatch(
          saveSettings({
            siteConfig: { ...siteConfig, version: 0 },
          }),
        );
      }
    }
  }, [adminMode]);

  return (
    <MainSiteConfigRequired siteConfig={siteConfig}>
      {children}
    </MainSiteConfigRequired>
  );
};

const MainSiteConfigRequired = ({ children, siteConfig }) => {
  const { customers } = useSelector((state) => state.customers);
  const dispatch = useDispatch();
  const { data, error, mutate } = useSWR(
    `${coreConfig.urls.site.config}?version=${siteConfig.version || 0}`,
    apiGet,
  );

  useEffect(() => {
    if (data) {
      localStorage.setItem("config", JSON.stringify(data.data.config));
      localStorage.setItem("customers", JSON.stringify(data.data.customers));
      dispatch(loadCustomers());
      dispatch(saveSettings({ siteConfig: data.data.config }));
    }
    if (error && !customers && siteConfig.version !== 0) {
      // reset site config version to 0
      dispatch(
        saveSettings({
          siteConfig: { ...siteConfig, version: 0 },
        }),
      );
      mutate();
    }
  }, [data, error]);
  return <>{children}</>;
};
